import { Button, Tooltip } from '@cb/apricot-react';
import { Grid } from '@mui/material';
import { styled } from '@mui/system';
import { FC } from 'react';

import { useToasts } from 'src/hooks';
import { ISubmitHistory, ISummary, TAIForm, TAIFormSubmit } from 'src/types';
import { formatDay, formatDate, uploadSchoolFile } from 'src/utils';
import { AttachmentList, FileUploader, SubmitHistory } from 'src/components/common';
import { useAuth } from 'src/contexts';
import { useAIFormStatus } from './hooks';

const ActionButton = styled(Button)`
  padding: 8px 16px;
`;

interface AIFormCardProps {
  school: ISummary;
  onClickDelete: VoidFunction;
  onClickUpdateAndSubmit: VoidFunction;
  onClickUpdateAndReSubmit: VoidFunction;
  onClickView: VoidFunction;
  onClickViewSubmit: (submit: ISubmitHistory) => void;
  onRefresh: VoidFunction;
}

const AIFormCard: FC<AIFormCardProps> = ({
  school,
  onClickDelete,
  onClickUpdateAndSubmit,
  onClickUpdateAndReSubmit,
  onClickView,
  onClickViewSubmit,
  onRefresh,
}) => {
  const tooltipId = school.categoryId;
  const isAllowedToUploadFile =
    school.processStatus !== 'processing' &&
    school.processStatus !== 'completed' &&
    school.processStatus !== 'rejected';

  const { userName } = useAuth();
  const { status, statusTooltip } = useAIFormStatus(school);
  const { render: renderToasts, toastErrorMessage, toastSuccessMessage } = useToasts();

  const renderButtons = () => {
    switch (school.processStatus) {
      case 'started':
        return (
          <>
            <li>
              <ActionButton onClick={onClickUpdateAndSubmit}>Update and Submit</ActionButton>
            </li>
            <li>
              <ActionButton onClick={onClickDelete}>Delete</ActionButton>
            </li>
          </>
        );

      case 'submitted':
      case 'reopen':
        return (
          <li>
            <ActionButton onClick={onClickUpdateAndReSubmit}>Update and Re-Submit</ActionButton>
          </li>
        );
      case 'rejected':
        return school.appealEligibleUpdates ? (
          <>
            {school?.appealEligibleUpdates === 'true' && (
              <li>
                <ActionButton onClick={onClickUpdateAndReSubmit}>Update and Re-Submit</ActionButton>
              </li>
            )}

            {school?.appealEligibleUpdates === 'false' && (
              <li>
                <ActionButton onClick={onClickView}>View</ActionButton>
              </li>
            )}
          </>
        ) : (
          <li>
            <ActionButton onClick={onClickUpdateAndReSubmit}>Update and Re-Submit</ActionButton>
          </li>
        );

      case 'processing':
      case 'completed':
      case 'appeal':
      case 'deactivationApproval':
      case 'international':
      case 'testSecurity':
      case 'opsApproved':
        return (
          <li>
            <ActionButton onClick={onClickView}>View</ActionButton>
          </li>
        );

      default:
        return null;
    }
  };

  const handleUploadFile = async (file: File, description: string, path?: string) => {
    try {
      const fileS3Key = await uploadSchoolFile(userName, school, file, description, path);
      toastSuccessMessage('File uploaded successfully.');
      onRefresh();
      return fileS3Key;
    } catch (error: any) {
      toastErrorMessage(error.message);
      return null;
    }
  };

  return (
    <div className="cb-card cb-card-event">
      {renderToasts()}

      <div className="cb-card-content">
        <Grid container>
          <Grid item xs={4}>
            <h2 className="cb-card-title">{school.schoolName}</h2>
            <div className="cb-card-desc">
              <span className="font-bold">Created:</span> {formatDay(school.createDate)}
            </div>
            <div className="cb-card-desc">
              <span className="font-bold">Last Updated:</span> {formatDate(school.updateDate)}
            </div>
            <div className="cb-card-desc">
              <div id={tooltipId}>{status}</div>
              {/** @ts-ignore */}
              <Tooltip placement="top" trigger={tooltipId}>
                <p>{statusTooltip}</p>
              </Tooltip>
            </div>
            <div className="cb-card-action">
              <ul className="cb-meta-tags">{renderButtons()}</ul>
            </div>
          </Grid>

          <Grid item xs={6}>
            <h2 className="cb-card-title">Supporting Materials</h2>
            <AttachmentList data={school} />
            {isAllowedToUploadFile && <FileUploader onUploadFile={handleUploadFile} uploadButton="Upload file" />}
          </Grid>

          <Grid item xs={2}>
            <Grid container justifyContent="flex-end">
              <Grid item xs={12}>
                <h2 className="cb-card-title cb-align-right">Submit History</h2>
              </Grid>

              <SubmitHistory school={school} onViewSubmit={onClickViewSubmit} />
            </Grid>
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

export default AIFormCard;
